import type { EventBus } from "./eventBus.js";
import { getRetryDelayMs, shouldRetry } from "./retryManager.js";
import { withTimeout } from "./timeoutManager.js";
import type { JobProgress, JobRecord, JobResult, JobTask } from "../types/job.js";
import type { RetryConfig } from "../types/retry.js";
import type { StorageAdapter } from "../types/storage.js";
import { JobCancelledError, JobTimeoutError, serializeError } from "../utils/errors.js";
import { delay, nowIso } from "../utils/time.js";

export type RunnerOptions<TPayload = unknown, TResult = unknown> = {
  record: JobRecord<TPayload, TResult>;
  task: JobTask<TPayload, TResult>;
  retry?: RetryConfig;
  timeoutMs: number;
  signal?: AbortSignal;
  lockKey?: string;
  lockTtlMs?: number;
  storage: StorageAdapter;
  eventBus: EventBus;
  acquireLock?: (lockKey: string, ttlMs: number) => Promise<boolean>;
  releaseLock?: (lockKey: string) => Promise<void>;
};

export class JobRunner {
  private activeCount = 0;

  getActiveCount(): number {
    return this.activeCount;
  }

  async run<TPayload, TResult>(options: RunnerOptions<TPayload, TResult>): Promise<JobResult<TResult>> {
    const { record, storage, eventBus } = options;
    const lockKey = options.lockKey;
    let locked = false;
    if (lockKey && options.acquireLock) {
      locked = await options.acquireLock(lockKey, options.lockTtlMs ?? options.timeoutMs);
      if (!locked) {
        const skipped: JobResult<TResult> = {
          jobId: record.id,
          name: record.name,
          status: "scheduled",
          attempt: record.attempts,
          durationMs: 0,
        };
        return skipped;
      }
    }

    this.activeCount += 1;
    try {
      let attempt = record.attempts;
      while (true) {
        attempt += 1;
        const startedAt = new Date();
        await storage.updateJob(record.id, {
          status: "running",
          attempts: attempt,
          startedAt: startedAt.toISOString(),
          lastRunAt: startedAt.toISOString(),
        });
        eventBus.emit("job:started", { jobId: record.id, name: record.name, attempt });

        try {
          const result = await withTimeout(
            async (timeoutSignal) => {
              const signal = mergeSignals(options.signal, timeoutSignal);
              if (signal.aborted) throw signal.reason ?? new JobCancelledError();
              return options.task({
                jobId: record.id,
                name: record.name,
                payload: record.payload as TPayload,
                attempt,
                signal,
                createdAt: new Date(record.createdAt),
                startedAt,
                progress: (progress) => this.reportProgress(record, progress, storage, eventBus),
              });
            },
            options.timeoutMs,
            options.signal
          );

          const finishedAt = nowIso();
          const durationMs = Date.now() - startedAt.getTime();
          const finalResult: JobResult<TResult> = {
            jobId: record.id,
            name: record.name,
            status: "success",
            result,
            attempt,
            durationMs,
            startedAt: startedAt.toISOString(),
            finishedAt,
          };
          const latest = await storage.getJob(record.id);
          await storage.updateJob(record.id, {
            status: "success",
            result,
            finishedAt,
            successCount: (latest?.successCount ?? record.successCount) + 1,
          });
          eventBus.emit("job:success", finalResult);
          return finalResult;
        } catch (error) {
          const finishedAt = nowIso();
          const durationMs = Date.now() - startedAt.getTime();
          const status = error instanceof JobTimeoutError ? "timeout" : isAbortError(error) ? "cancelled" : "failed";
          const finalResult: JobResult<TResult> = {
            jobId: record.id,
            name: record.name,
            status,
            error: serializeError(error),
            attempt,
            durationMs,
            startedAt: startedAt.toISOString(),
            finishedAt,
          };

          if (status === "cancelled" || status === "timeout" || !shouldRetry(error, attempt, options.retry)) {
            const latest = await storage.getJob(record.id);
            await storage.updateJob(record.id, {
              status,
              error: finalResult.error,
              finishedAt,
              failureCount: (latest?.failureCount ?? record.failureCount) + 1,
            });
            if (status === "timeout") {
              eventBus.emit("job:timeout", finalResult);
            } else if (status === "cancelled") {
              eventBus.emit("job:cancelled", { jobId: record.id, name: record.name });
            } else {
              eventBus.emit("job:failed", finalResult);
            }
            return finalResult;
          }

          const retryDelayMs = getRetryDelayMs(attempt, options.retry);
          eventBus.emit("job:retry", { jobId: record.id, name: record.name, attempt, delayMs: retryDelayMs });
          await delay(retryDelayMs, options.signal);
        }
      }
    } finally {
      this.activeCount -= 1;
      if (locked && lockKey && options.releaseLock) await options.releaseLock(lockKey);
    }
  }

  private async reportProgress(
    record: JobRecord,
    progress: Omit<JobProgress, "at">,
    storage: StorageAdapter,
    eventBus: EventBus
  ): Promise<void> {
    const normalized: JobProgress = {
      ...progress,
      percent: typeof progress.percent === "number" ? Math.max(0, Math.min(100, progress.percent)) : undefined,
      at: nowIso(),
    };
    await storage.updateJob(record.id, { progress: normalized });
    eventBus.emit("job:progress", { jobId: record.id, name: record.name, progress: normalized });
  }
}

function mergeSignals(a?: AbortSignal, b?: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const abort = (signal: AbortSignal) => controller.abort(signal.reason);
  if (a?.aborted) abort(a);
  if (b?.aborted) abort(b);
  a?.addEventListener("abort", () => abort(a), { once: true });
  b?.addEventListener("abort", () => abort(b), { once: true });
  return controller.signal;
}

function isAbortError(error: unknown): boolean {
  return error instanceof JobCancelledError || (error instanceof Error && error.name === "AbortError");
}
